// Конкретный платёж месяца: что, сколько, какого числа и повторяется ли.

import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { BudgetPayment, Category } from '../api/types';
import { formatMinor, parseAmountToMinor } from '../lib/money';
import { haptic } from '../lib/telegram';
import { humanError } from '../lib/errors';
import { useToast } from '../lib/toast';
import { SheetCta } from './SheetCta';
import { useBackButton, useMainButton } from '../lib/use-main-button';

type Props = {
  period: string;
  initial: BudgetPayment | null;
  categories: Category[];
  onClose: () => void;
  onSaved: () => void;
};

export function BudgetPaymentSheet({
  period,
  initial,
  categories,
  onClose,
  onSaved,
}: Props): JSX.Element {
  const isEdit = initial != null;
  const [label, setLabel] = useState(initial?.label ?? '');
  // formatMinor, а не деление на 100n: целочисленное деление срезало копейки,
  // и правка платежа 1 500 000,50 молча сохраняла 1 500 000,00.
  const [amountMajor, setAmountMajor] = useState(
    initial ? formatMinor(initial.amount_minor, initial.currency) : '',
  );
  const [categoryId, setCategoryId] = useState(initial?.category_id ?? '');
  const [dueDay, setDueDay] = useState(initial?.due_day != null ? String(initial.due_day) : '');
  const [isRecurring, setIsRecurring] = useState(initial?.is_recurring ?? true);
  const [note, setNote] = useState(initial?.note ?? '');

  useBackButton(onClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const minor = parseAmountToMinor(amountMajor);
  const dayNum = dueDay === '' ? null : Number(dueDay);
  const dayValid = dayNum === null || (Number.isInteger(dayNum) && dayNum >= 1 && dayNum <= 31);
  const isValid = label.trim().length > 0 && minor !== null && minor > 0n && dayValid;

  const toast = useToast();
  const saveMut = useMutation({
    // Создание и правка возвращают разное тело; наверх нужен только факт успеха.
    mutationFn: async (): Promise<void> => {
      if (minor === null) throw new Error('bad amount');
      const body = {
        label: label.trim(),
        category_id: categoryId || null,
        amount_minor: minor.toString(),
        due_day: dayNum,
        is_recurring: isRecurring,
        note: note.trim() || null,
      };
      if (isEdit && initial) await api.updateBudgetPayment(initial.id, body);
      else await api.createBudgetPayment({ period, ...body });
    },
    onSuccess: () => {
      haptic('success');
      onSaved();
      onClose();
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось сохранить платёж')),
  });

  const cta = useMainButton({
    text: isEdit ? 'Сохранить' : 'Добавить платёж',
    onClick: () => {
      if (!isValid) return;
      saveMut.mutate();
    },
    enabled: isValid && !saveMut.isPending,
    progress: saveMut.isPending,
  });

  const expenseCats = categories.filter((c) => c.kind === 'expense');

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="sheet-handle" />
        <div className="sheet-title">{isEdit ? 'Изменить платёж' : 'Новый платёж'}</div>

        <label className="form-field">
          <span className="form-label">Название</span>
          <input
            type="text"
            className="form-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={120}
            placeholder="Например: Аренда"
            autoFocus={!isEdit}
          />
        </label>

        <div className="row-2">
          <label className="form-field">
            <span className="form-label">Сумма (UZS)</span>
            <input
              type="text"
              inputMode="decimal"
              className={`form-input ${minor === null && amountMajor ? 'invalid' : ''}`}
              value={amountMajor}
              onChange={(e) => setAmountMajor(e.target.value)}
              placeholder="2 500 000"
            />
          </label>

          <label className="form-field">
            <span className="form-label">Число месяца</span>
            <input
              type="text"
              inputMode="numeric"
              className={`form-input ${!dayValid ? 'invalid' : ''}`}
              value={dueDay}
              onChange={(e) => setDueDay(e.target.value.replace(/\D/g, '').slice(0, 2))}
              placeholder="5"
            />
          </label>
        </div>

        <label className="form-field">
          <span className="form-label">Категория</span>
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="form-select"
          >
            <option value="">Без категории</option>
            {expenseCats.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <span className="form-hint">Нужна, чтобы платёж попал в план по этой категории.</span>
        </label>

        <button
          type="button"
          className={`switch-row ${isRecurring ? 'on' : ''}`}
          onClick={() => {
            haptic('light');
            setIsRecurring((v) => !v);
          }}
          aria-pressed={isRecurring}
        >
          <span className="switch-track">
            <span className="switch-knob" />
          </span>
          <span className="switch-text">
            <span className="switch-title">Повторяется каждый месяц</span>
            <span className="switch-sub">Перенесётся при сборке следующего месяца</span>
          </span>
        </button>

        <label className="form-field">
          <span className="form-label">Заметка</span>
          <input
            type="text"
            className="form-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
            placeholder="Необязательно"
          />
        </label>

        <div className="sheet-actions">
          <SheetCta {...cta} />
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={saveMut.isPending}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
